/* The only place the API stage names and the operator-facing labels meet. */

import { localizedRecord } from "../i18n/localized";

export const OUTBOUND_STAGES = [
  "erp_detected",
  "mapped",
  "built",
  "validated",
  "recorded",
  "sent",
  "tracking",
  "published",
  "archived",
] as const;
export type OutboundStage = (typeof OUTBOUND_STAGES)[number];

export const INBOUND_STAGES = [
  "received",
  "routed",
  "validated",
  "archived",
  "mapped",
  "landed_draft",
] as const;
export type InboundStage = (typeof INBOUND_STAGES)[number];

export const OUTBOUND_OFF_RAMPS = ["error"] as const;
export const INBOUND_OFF_RAMPS = ["held_unrouted", "held_exception", "rejected"] as const;

export const STAGE_LABEL: Record<OutboundStage, string> = localizedRecord("domain.stage", {
  erp_detected: "ERP invoice",
  mapped: "Collect & map",
  built: "Build XML",
  validated: "Validate",
  recorded: "Record",
  sent: "ASP / Peppol",
  tracking: "Track outcome",
  published: "Result published",
  archived: "Archive",
});

export const STAGE_SHORT: Record<OutboundStage, string> = localizedRecord("domain.stageShort", {
  erp_detected: "ERP",
  mapped: "Map",
  built: "XML",
  validated: "Validate",
  recorded: "Record",
  sent: "Send",
  tracking: "Outcome",
  published: "Result",
  archived: "Archive",
});

export const STAGE_NOTE: Record<OutboundStage, string> = localizedRecord("domain.stageNote", {
  erp_detected: "Invoice and credit-note data",
  mapped: "Apply the entity mapping profile",
  built: "UBL 2.1 · UUID · QR information",
  validated: "Apply PINT-OM rules before sending",
  recorded: "Store XML and audit trail before anything is sent",
  sent: "Transmit the valid XML",
  tracking: "Acknowledgement and final status",
  published: "UUID, status and QR held on the interface for the ERP to collect",
  archived: "Long-term legal record, with the acknowledgements",
});

export const IN_STAGE_LABEL: Record<InboundStage, string> = localizedRecord("domain.inStage", {
  received: "Supplier sends",
  routed: "Route to entity",
  validated: "Validate",
  archived: "Archive original",
  mapped: "Create draft",
  landed_draft: "Finance review",
});

/* The operator-facing strip on /inbound; IN_STAGE_LABEL is the explainer wording. */
export const INBOUND_STAGE_LABEL: Record<InboundStage, string> = localizedRecord("domain.inboundStage", {
  received: "Received",
  routed: "Routed",
  validated: "Validated",
  archived: "Archived",
  mapped: "Mapped",
  landed_draft: "Draft created",
});

export function inboundStageIndex(stage: string): number {
  return INBOUND_STAGES.indexOf(stage as InboundStage);
}

export const IN_STAGE_NOTE: Record<InboundStage, string> = localizedRecord("domain.inStageNote", {
  received: "Document arrives via ASP / Peppol",
  routed: "Match participant ID to the legal entity",
  validated: "Structure, identity and content checks",
  archived: "Preserve the legal XML record",
  mapped: "Draft purchase invoice in the receiving ERP",
  landed_draft: "Entity team reviews and posts manually",
});

export function stageIndex(stage: OutboundStage): number {
  return OUTBOUND_STAGES.indexOf(stage);
}
